import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Drawer, TextField, MenuItem, ToggleButtonGroup, ToggleButton } from '@mui/material';
import PositionTable from '../Positions/ViewPositionsPage/PositionTable';
import { StoneXNumber, StoneXAutocomplete, StoneXDatePicker, StoneXTextField, StoneXContractMonthDropdown } from '../../../StoneXMui';
import dayjs from 'dayjs';
import { AllocationModel, PositionModel, UpdatePositionModel, UpdateDealTicketRequest, ContractDate, Unit, Currency, Side, UpdateAllocationRequest } from '../../../../Generated/Commodity-Management-Api';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import UploadEfpContractModal from '../Efp/UploadEfpContractModal';
import EfpContractsPanel from '../Efp/EfpContractsPanel';

interface PositionsTabProps {
    futurePositionsByContractDate: Record<string, any[]>;
    detailsVisibility: Record<string, boolean>;
    setDetailsVisibility: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    getFuturesForEfp: (positions: any[]) => void;
    getPositionsForOffset: (positions: any[]) => void;
    customerSettings: any;
    columnsToShow: any;
    editDealTicket: (dealTicketId?: number) => void;
    setPositionValidation: (dealTicketId: number) => void;
    viewAuditTrail: (positionId: number) => void;
    positionsByAllocation:  PositionModel[];
    units: Unit []; // Add this prop
    allocations: AllocationModel[]; // Add this prop
    allPositions: PositionModel[];
    onCustomDataKeysChange?: (keys: string[]) => void;
}

const PositionsTab: React.FC<PositionsTabProps> = ({
    futurePositionsByContractDate,
    detailsVisibility,
    setDetailsVisibility,
    getFuturesForEfp,
    getPositionsForOffset,
    customerSettings,
    columnsToShow,
    editDealTicket,
    setPositionValidation,
    viewAuditTrail,
    positionsByAllocation,
    units,
    allocations,
    allPositions,
    onCustomDataKeysChange
}) => {
    const [filteredPositions, setFilteredPositions] = useState<PositionModel[] | null>(null);

    // Use filteredPositions for totals if available, else positionsByAllocation
    const positionsForTotals = filteredPositions && filteredPositions.length > 0 ? filteredPositions : positionsByAllocation;

    // Entry screen state
    const [entryOpen, setEntryOpen] = useState(false);
    const [positionType, setPositionType] = useState<'Physical' | 'Basis' | 'Future'>('Physical');
    const [price, setPrice] = useState('');
    const [quantity, setQuantity] = useState('');
    const [unit, setUnit] = useState<Unit | null>(null);
    const [side, setSide] = useState<'Buy' | 'Sell' >('Buy');
    const [currency, setCurrency] = useState<'USD' | 'CAD'>('USD');
    const [allocation, setAllocation] = useState(allocations.length > 0 ? allocations[0] : null);
    const [isSaving, setIsSaving] = useState(false);
    const [budgetDates, setBudgetDates] = useState<Record<number, { month: number; year: number }>>({});
    const [budgetDate, setBudgetDate] = useState<ContractDate | null>(null);
    const [contractDate, setContractDate] = useState<ContractDate | null>(null);
    const [tradeDate, setTradeDate] = useState<Date | null>(dayjs().toDate());
    const [tradeDateError, setTradeDateError] = useState<string>();
    const [dealTicketNumber, setDealTicketNumber] = useState<string>('');

    // Add state for upload modal
    const [uploadWordOpen, setUploadWordOpen] = useState(false);

    const onTradeDateChange = (value: Date | null): void => setTradeDate(value);

    // Example: set current commodity/location from props if available
    useEffect(() => {
        // Replace with actual current values if available from props
        setUnit(units.length > 0 ? units[0] : null);
        setAllocation(allocations.length > 0 ? allocations[0] : null);
    }, [units, allocations]);

    const handleEntrySubmit = async () => {
        setIsSaving(true);
        const promises = [];

        const promise = async () => {
            const newPosition: UpdatePositionModel = {
                positionId: undefined,
                volume: Number(quantity),
                locationId: allocation?.location.locationId,
                budgetDate: budgetDate!,
                contractDate: contractDate!,
                price: Number(price),
                unit: unit!,
                side: side!,
                currency: currency!,
            };

            const physicalDealTicket: UpdateDealTicketRequest = {
                tradeDate: dayjs(tradeDate).format('MM/DD/YYYY'),
                commodityId: allocation?.commodity.commodityId!,
                counterpartyId: allocation?.counterparty.counterpartyId!,
                dealTicketNumber: dealTicketNumber,
                positions: [newPosition],
            };

            const tradeResponse = await CommodityManagementApi.addTrade(null, physicalDealTicket);
            const { data: createdPositions } = await CommodityManagementApi.getDealTicket(null, tradeResponse.data.dealTicketId!.toString());

            // Create new allocations for the newly created positions
            const allocationPromises = createdPositions.dealTicket!.positions.map((p: PositionModel) => {
                const newAllocation: UpdateAllocationRequest = {
                    allocationId: 0,
                    allocationGroupId: Number(allocation?.allocationGroupId),
                    positionId: p.positionId!,
                    volume: Number(quantity),
                };
                return CommodityManagementApi.addAllocation(null, newAllocation);
            });

            await Promise.all(allocationPromises);
        };
        promises.push(promise());

        try {
            await Promise.all(promises);
            // Reset fields and close drawer
            setEntryOpen(false);
            setPositionType('Physical');
            setPrice('');
            setQuantity('');
            setUnit(units.length > 0 ? units[0] : null);
            setSide('Buy');
            setCurrency('USD');
            setAllocation(allocations.length > 0 ? allocations[0] : null);
            setBudgetDate(null);
            setContractDate(null);
            setTradeDate(dayjs().toDate());
            setTradeDateError(undefined);
            setDealTicketNumber('');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Typography variant="h6" gutterBottom>Futures</Typography>
            {Object.entries(futurePositionsByContractDate).map(([contractDate, positions]) => {
                const totalVolume = positions.reduce((sum, p) => sum + ((p.volume ?? 0) * (p.side === "Buy" ? 1 : -1)), 0);
                const totalValue = positions.reduce((sum, p) => sum + ((p.volume ?? 0) * (p.price ?? 0) * (p.side === "Buy" ? 1 : -1)), 0);
                const weightedAveragePrice = totalVolume !== 0 ? totalValue / totalVolume : 0;
                const marketValue = positions.reduce((sum, p) => sum + ((p.volume ?? 0) * (p.marketPrice ?? 0) * (p.side === "Buy" ? 1 : -1)), 0);
                const weightedMarketPrice = totalVolume !== 0 ? marketValue / totalVolume : 0;
                const mtmValue = positions.reduce((sum, p) => sum + ((p.markToMarketValue ?? 0)), 0);

                return (
                    <Box key={contractDate} sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }} className="position-card2">
                            <Typography variant="subtitle1" className="position-card-text" sx={{ minWidth: 120 }}>
                                {contractDate}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" className="position-card-text2">
                                        Total Volume:
                                    </Typography>
                                    <Typography variant="body2" className="position-card-text2">
                                        {totalVolume.toLocaleString()}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" className="position-card-text2">
                                        Weighted Price:
                                    </Typography>
                                    <Typography variant="body2" className="position-card-text2">
                                        <StoneXNumber number={weightedAveragePrice} currency="USD" decimals={2} />
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" className="position-card-text2">
                                        Market Price:
                                    </Typography>
                                    <Typography variant="body2" className="position-card-text2">
                                        <StoneXNumber number={weightedMarketPrice} currency="USD" decimals={2} />
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" className="position-card-text2">
                                        Mtm Value:
                                    </Typography>
                                    <Typography variant="body2" className="position-card-text2">
                                        <StoneXNumber number={mtmValue} currency="USD" decimals={0} colored={true} />
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Button variant="outlined" onClick={() => setDetailsVisibility(prev => ({ ...prev, [contractDate]: !prev[contractDate] }))}>
                                {detailsVisibility[contractDate] ? 'Hide Details' : 'Show Details'}
                            </Button>
                            <Button variant="outlined" onClick={() => getFuturesForEfp(positions)}>EFP</Button>
                            <Button variant="outlined" onClick={() => getPositionsForOffset(positions)}>Offset</Button>
                        </Box>
                        {detailsVisibility[contractDate] && <PositionTable
                            positions={positions}
                            customerSettings={customerSettings}
                            columnsToShow={columnsToShow}
                            onEditPosition={editDealTicket}
                            onSetPositionValidation={setPositionValidation}
                            onViewAuditTrail={viewAuditTrail}
                            onCustomDataKeysChange={onCustomDataKeysChange}
                        />}
                    </Box>
                );
            })}

            <Typography variant="h6" gutterBottom>Assigned Positions</Typography>
                    <Box sx={{ mb: 4 }}>
                        {/* Totals using positionsForTotals */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }} className="position-card2">
                            <Typography variant="subtitle1" className="position-card-text" sx={{ minWidth: 120 }}>
                                Totals
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" className="position-card-text2">
                                        Total Volume:
                                    </Typography>
                                    <Typography variant="body2" className="position-card-text2">
                                        {positionsForTotals.reduce((sum, p) => sum + ((p.volume ?? 0) * (p.side === "Buy" ? 1 : -1)), 0).toLocaleString()}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" className="position-card-text2">
                                        Weighted Price:
                                    </Typography>
                                    <Typography variant="body2" className="position-card-text2">
                                        <StoneXNumber number={positionsForTotals.reduce((sum, p) => sum + ((p.volume ?? 0) * (p.price ?? 0) * (p.side === "Buy" ? 1 : -1)), 0) / positionsForTotals.reduce((sum, p) => sum + ((p.volume ?? 0) * (p.side === "Buy" ? 1 : -1)), 0)} currency="USD" decimals={2} />
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" className="position-card-text2">
                                        Market Price:
                                    </Typography>
                                    <Typography variant="body2" className="position-card-text2">
                                        <StoneXNumber number={positionsForTotals.reduce((sum, p) => sum + ((p.volume ?? 0) * (p.marketPrice ?? 0) * (p.side === "Buy" ? 1 : -1)), 0) / positionsForTotals.reduce((sum, p) => sum + ((p.volume ?? 0) * (p.side === "Buy" ? 1 : -1)), 0)} currency="USD" decimals={2} />
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" className="position-card-text2">
                                        Mtm Value:
                                    </Typography>
                                    <Typography variant="body2" className="position-card-text2">
                                        <StoneXNumber number={positionsForTotals.reduce((sum, p) => sum + ((p.markToMarketValue ?? 0)), 0)} currency="USD" decimals={0} colored={true} />
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <PositionTable positions={positionsByAllocation}
                            customerSettings={customerSettings}
                            columnsToShow={columnsToShow}
                            onEditPosition={editDealTicket}
                            onSetPositionValidation={setPositionValidation}
                            onViewAuditTrail={viewAuditTrail}
                            onCustomDataKeysChange={onCustomDataKeysChange}
                            onFilteredPositionsChange={setFilteredPositions} />
                    </Box>
            <Button variant="outlined" sx={{ position: 'fixed', right: 32, top: 250, zIndex: 1200 }} onClick={() => setEntryOpen(true)}>
                Add Position
            </Button>
            <Drawer anchor="right" open={entryOpen} onClose={() => setEntryOpen(false)}>
                <Box sx={{ width: 350, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h6">Add Position</Typography>
                    <ToggleButtonGroup
                        value={positionType}
                        exclusive
                        onChange={(_, val) => val && setPositionType(val)}
                        sx={{ mb: 2 }}
                    >
                        <ToggleButton value="Physical">Physical</ToggleButton>
                        <ToggleButton value="Basis">Basis</ToggleButton>
                        <ToggleButton value="Future">Future</ToggleButton>
                    </ToggleButtonGroup>
                    <ToggleButtonGroup
                        value={side}
                        exclusive
                        onChange={(_, val) => val && setSide(val)}
                        sx={{ mb: 2 }}
                    >
                        <ToggleButton value="Buy">Buy</ToggleButton>
                        <ToggleButton value="Sell">Sell</ToggleButton>
                    </ToggleButtonGroup><ToggleButtonGroup
                        value={currency}
                        exclusive
                        onChange={(_, val) => val && setCurrency(val)}
                        sx={{ mb: 2 }}
                    >
                        <ToggleButton value="USD">USD</ToggleButton>
                        <ToggleButton value="CAD">CAD</ToggleButton>
                    </ToggleButtonGroup>
                    <StoneXDatePicker
                        value={tradeDate}
                        onChange={onTradeDateChange}
                        label="Trade Date"
                        error={tradeDateError}
                        disabled={isSaving}
                    />
                    <StoneXContractMonthDropdown value={budgetDate} label="Budget Date" onChange={setBudgetDate} width='small' />
                    <StoneXContractMonthDropdown value={contractDate} label="Contract Date" onChange={setContractDate} width='small' />
                    <StoneXTextField
                        type="text"
                        label="Purchase Order"
                        onChange={(event) => setDealTicketNumber(event.target.value)}
                        disabled={isSaving}
                        fullWidth
                    />
                    <StoneXTextField
                        label="Price"
                        value={price}
                        onChange={e => setPrice(e.target.value)}
                        type="number"
                        fullWidth
                    />
                    <StoneXTextField
                        label="Quantity"
                        value={quantity}
                        onChange={e => setQuantity(e.target.value)}
                        type="number"
                        fullWidth
                    />
                    <StoneXAutocomplete
                        options={units ?? []}
                        getOptionLabel={(q) => q}
                        value={allocation?.unit}
                        onChange={(e: any, u: Unit | null) => setUnit(u)}
                        disabled={isSaving}
                        label="Unit"
                    />
                    <StoneXAutocomplete
                        options={allocations ?? []}
                        getOptionLabel={(q) => q.counterparty.counterpartyName}
                        value={allocation}
                        onChange={(e: any, alloc: AllocationModel | null) => setAllocation(alloc)}
                        disabled={isSaving}
                        isOptionEqualToValue={(a, b) => a.allocationGroupId == b.allocationGroupId}
                        label="Supplier"
                    />
                    <Button variant="contained" onClick={handleEntrySubmit}>Submit</Button>
                </Box>
            </Drawer>

            {/* Add button for Word upload alongside the existing Add Position button */}
            <Button 
                variant="outlined" 
                sx={{ position: 'fixed', right: 32, top: 300, zIndex: 1200 }} 
                onClick={() => setUploadWordOpen(true)}
            >
                Upload Contract
            </Button>

            {/* Confirmations stored against the deal tickets they created. */}
            <EfpContractsPanel positions={allPositions} />

            {/* Add the upload modal */}
            <UploadEfpContractModal
                open={uploadWordOpen}
                onClose={() => setUploadWordOpen(false)}
                onSaved={() => {
                    setUploadWordOpen(false);
                    // Optionally refresh positions here
                }}
                allocations={allocations}
                positions={allPositions}
            />
        </>
    );
};

export default PositionsTab;